import React, { useState, useMemo } from 'react';
import { useGame } from '../../context/GameContext';
import { resources, buildings, technologies } from '../../data';
import {
  // Main quests
  mainQuests,
  getMainQuestIds,
  chapters,
  getMainQuestsByChapter,
  getMainChapterProgress,
  // Side quests
  sideQuests,
  sideQuestChains,
  getSideQuestsByChain,
  getSideChainProgress,
  getAvailableSideQuests,
  // Extra quests
  extraQuests,
  extraQuestCategories,
  getExtraQuestsByCategory,
  getExtraCategoryProgress,
  getAvailableExtraQuests,
  // Universal helpers
  getTotalQuestCounts,
  QUEST_CATEGORIES,
} from '../../data/quests';
import { getScaledQuestRewards, getScaledQuestObjectives } from '../../data/balance';
import { ResourceChip } from '../common';
import BuildingChip from '../common/BuildingChip';
import TechChip from '../common/TechChip';

// Chapter icons for visual flair
const CHAPTER_ICONS = {
  1: '🌅', 2: '🏠', 3: '⚔️', 4: '💰', 5: '🏭',
  6: '⚡', 7: '👑', 8: '🔬', 9: '✨', 10: '🌟'
};

function QuestPanel({ onNotification, onNavigateToBuilding, onNavigateToTech, onNavigateToWorldMap }) {
  const {
    playerResources,
    buildingCounts,
    researchedTechs,
    activeQuest,
    completedQuests = [],
    completedSideQuests = [],
    completedExtraQuests = [],
    isQuestComplete,
    completeQuest,
    completeSideQuest,
    completeExtraQuest,
    trackedQuest,
    setTrackedQuest,
    worldMap,
    playerCharacter,
    builtWonders = [],
    activeAdvisors = [],
  } = useGame();

  const [showQuestLog, setShowQuestLog] = useState(false);
  const [selectedMainQuest, setSelectedMainQuest] = useState(null);
  const [selectedSideQuest, setSelectedSideQuest] = useState(null);
  const [selectedExtraQuest, setSelectedExtraQuest] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(1);
  const [questLogTab, setQuestLogTab] = useState('main'); // 'main', 'side', 'extra'
  const [selectedChain, setSelectedChain] = useState('explorer');
  const [selectedCategory, setSelectedCategory] = useState('milestone');

  const questIds = getMainQuestIds();
  const currentIndex = activeQuest ? questIds.indexOf(activeQuest) : questIds.length;

  // Calculate total buildings for buildings_total objective
  const totalBuildings = useMemo(() => 
    Object.values(buildingCounts).reduce((sum, count) => sum + count, 0),
    [buildingCounts]
  );

  // World map stats for exploration quests
  const tilesExplored = Object.keys(worldMap?.discoveredNodes || {}).length;
  const tilesBuilt = (worldMap?.builtNodeIds || []).length;
  const expeditionHistory = worldMap?.expeditionHistory || {};
  const expeditionsTotal = Object.values(expeditionHistory).reduce((sum, h) => sum + (h.count || 0), 0);

  // Player stats for combat/dungeon quests
  // Use playerCharacter.stats (not dungeonStats) to match GameContext storage
  const dungeonClears = playerCharacter?.stats?.dungeonsCleared || 0;
  const bossesDefeated = playerCharacter?.stats?.bossesDefeated || 0;
  const characterLevel = playerCharacter?.level || 1;

  // Available side/extra quests
  const playerState = useMemo(() => ({
    currentChapter: activeQuest ? (mainQuests[activeQuest]?.chapter || 1) : 10,
    researchedTechs: researchedTechs || [],
    buildingCounts: buildingCounts || {},
    tilesExplored,
    totalBuildings,
    dungeonClears,
    builtWonders,
    activeAdvisors,
  }), [activeQuest, researchedTechs, buildingCounts, tilesExplored, totalBuildings, dungeonClears, builtWonders, activeAdvisors]);

  const availableSideQuests = useMemo(() =>
    getAvailableSideQuests(completedSideQuests, completedQuests, playerState),
    [completedSideQuests, completedQuests, playerState]
  );

  const availableExtraQuests = useMemo(() =>
    getAvailableExtraQuests(completedExtraQuests, playerState),
    [completedExtraQuests, playerState]
  );

  const getQuestStatus = (qid) => {
    if (completedQuests.includes(qid)) return 'done';
    if (qid === activeQuest) return 'current';
    return 'locked';
  };

  // Check objective progress (uses scaled objectives from balance.js)
  const checkObjective = (obj, status, chapter = 1) => {
    // Get scaled version of this objective
    const [scaledObj] = getScaledQuestObjectives([obj], chapter);
    const target = scaledObj.count || 1;

    if (status === 'done') return { current: target, target, done: true };
    if (status === 'locked') return { current: 0, target, done: false };

    let current = 0, done = false;

    switch (obj.type) {
      case 'building':
        current = buildingCounts[obj.target] || 0;
        done = current >= target;
        break;
      case 'resource':
        current = Math.floor(playerResources[obj.target] || 0);
        done = current >= target;
        break;
      case 'tech':
        done = researchedTechs.includes(obj.target);
        current = done ? 1 : 0;
        break;
      case 'buildings_total':
        current = totalBuildings;
        done = current >= target;
        break;
      case 'tiles_explored':
        current = tilesExplored;
        done = current >= target;
        break;
      case 'tiles_built':
        current = tilesBuilt;
        done = current >= target;
        break;
      case 'expeditions_total':
        current = expeditionsTotal;
        done = current >= target;
        break;
      case 'expeditions_biome':
        current = expeditionHistory[obj.target]?.count || 0;
        done = current >= target;
        break;
      case 'kingdom_tier':
        current = 0;
        done = false;
        break;
      // Extra quest objective types
      case 'dungeons_cleared':
        current = dungeonClears;
        done = current >= target;
        break;
      case 'bosses_defeated':
        current = bossesDefeated;
        done = current >= target;
        break;
      case 'character_level':
        current = characterLevel;
        done = current >= target;
        break;
      case 'techs_researched':
        current = researchedTechs?.length || 0;
        done = current >= target;
        break;
      case 'equipment_slots_filled': {
        const equipment = playerCharacter?.equipment || {};
        current = Object.values(equipment).filter(e => e !== null).length;
        done = current >= target;
        break;
      }
      case 'equipment_rarity_min': {
        const rarityOrder = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];
        const minRarityIndex = rarityOrder.indexOf(obj.target);
        const inventory = playerCharacter?.inventory || [];
        const equipment = playerCharacter?.equipment || {};
        const allItems = [...inventory, ...Object.values(equipment).filter(Boolean)];
        current = allItems.filter(item => {
          const itemRarityIndex = rarityOrder.indexOf(item.rarity);
          return itemRarityIndex >= minRarityIndex;
        }).length;
        done = current >= target;
        break;
      }
      case 'equipment_max_stars': {
        const inventory = playerCharacter?.inventory || [];
        const equipment = playerCharacter?.equipment || {};
        const allItems = [...inventory, ...Object.values(equipment).filter(Boolean)];
        current = Math.max(0, ...allItems.map(item => item.starForce || 0));
        done = current >= target;
        break;
      }
      case 'unique_biomes_expeditions': {
        const uniqueBiomes = Object.keys(expeditionHistory).filter(biome =>
          (expeditionHistory[biome]?.count || 0) > 0
        ).length;
        current = uniqueBiomes;
        done = current >= target;
        break;
      }
      case 'main_quests_completed':
        current = completedQuests.length;
        done = current >= target;
        break;
      case 'dungeon_tier_cleared': {
        // Check if specific dungeon tier has been cleared
        // Use playerCharacter.stats.dungeonTierClears and obj.tier (not obj.target) to match GameContext
        const tierClears = playerCharacter?.stats?.dungeonTierClears || {};
        current = tierClears[obj.tier] || 0;
        done = current >= target;
        break;
      }
      case 'tech_count':
        current = researchedTechs?.length || 0;
        done = current >= target;
        break;
      case 'equipment_rarity_owned': {
        // Count equipment with at least the specified rarity
        const rarityOrder = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];
        const minRarityIndex = rarityOrder.indexOf(obj.rarity || obj.target); // Support both formats
        const inventory = playerCharacter?.inventory || [];
        const equipment = playerCharacter?.equipment || {};
        // Only count actual equipment (items with 'slot' property), not consumables/materials
        const allEquipment = [
          ...inventory.filter(i => i.slot),
          ...Object.values(equipment).filter(Boolean)
        ];
        current = allEquipment.filter(item =>
          item.rarity && rarityOrder.indexOf(item.rarity) >= minRarityIndex
        ).length;
        done = current >= target;
        break;
      }
      case 'equipment_stars_15_count': {
        const inventory = playerCharacter?.inventory || [];
        const equipment = playerCharacter?.equipment || {};
        const allItems = [...inventory, ...Object.values(equipment).filter(Boolean)];
        current = allItems.filter(item => (item.starForce || 0) >= 15).length;
        done = current >= target;
        break;
      }
      case 'equipment_scrolled': {
        const inventory = playerCharacter?.inventory || [];
        const equipment = playerCharacter?.equipment || {};
        const allItems = [...inventory, ...Object.values(equipment).filter(Boolean)];
        current = allItems.filter(item => (item.appliedScrolls?.length || 0) > 0).length;
        done = current >= target;
        break;
      }
      case 'great_works': {
        const greatWorkLocations = worldMap?.greatWorkLocations || {};
        current = Object.keys(greatWorkLocations).length;
        done = current >= target;
        break;
      }
      case 'wonders': {
        current = Array.isArray(builtWonders) ? builtWonders.length : Object.keys(builtWonders).length;
        done = current >= target;
        break;
      }
      case 'outposts': {
        const outposts = worldMap?.outposts || {};
        current = Object.keys(outposts).filter(key => !outposts[key].isConstructing).length;
        done = current >= target;
        break;
      }
      case 'mercenaries_hired': {
        const mercenaries = playerCharacter?.mercenaries || {};
        current = Object.keys(mercenaries).length;
        done = current >= target;
        break;
      }
      case 'mercenary_missions': {
        const missionStats = playerCharacter?.mercenaryMissionStats || {};
        current = missionStats.completed || 0;
        done = current >= target;
        break;
      }
      case 'trade_routes': {
        const tradeRoutes = worldMap?.tradeRoutes || [];
        current = tradeRoutes.length;
        done = current >= target;
        break;
      }
      case 'life_skill_level': {
        const lifeSkills = playerCharacter?.lifeSkills || {};
        const skillData = lifeSkills[obj.target] || {};
        current = skillData.level || 1;
        done = current >= target;
        break;
      }
      case 'crafting_completed': {
        const craftingStats = playerCharacter?.craftingStats || {};
        current = craftingStats.completed || 0;
        done = current >= target;
        break;
      }
      case 'advisors_hired': {
        current = activeAdvisors.length;
        done = current >= target;
        break;
      }
      case 'beacons_discovered': {
        const discoveredNodes = worldMap?.discoveredNodes || {};
        current = Object.values(discoveredNodes).filter(node => node.isBeacon).length;
        done = current >= target;
        break;
      }
      case 'encounters_cleared': {
        // Use playerCharacter.stats.encountersCompleted to match GameContext
        current = playerCharacter?.stats?.encountersCompleted || 0;
        done = current >= target;
        break;
      }
      default:
        break;
    }

    return { current, target, done };
  };

  // Render rich objective with chips
  const renderObjective = (obj, status, inModal = false, chapter = 1, callbacks = {}) => {
    const { onNavigateToBuilding: navToBuilding, onNavigateToTech: navToTech, onNavigateToWorldMap: navToWorldMap } = callbacks;
    const { current, target, done } = checkObjective(obj, status, chapter);
    const isLocked = status === 'locked';
    
    // Build the objective content with chips
    let content;
    switch (obj.type) {
      case 'building': {
        const building = buildings[obj.target];
        const isUnlocked = !building?.unlockCondition?.tech || researchedTechs.includes(building.unlockCondition.tech);

        content = (
          <>
            Build {obj.count}{' '}
            <span
              onClick={(e) => {
                e.stopPropagation();
                if (!isLocked) {
                  if (!isUnlocked && building?.unlockCondition?.tech && navToTech) {
                    // Building not unlocked - redirect to Research panel
                    navToTech(building.unlockCondition.tech);
                  } else if (navToBuilding) {
                    // Building unlocked - go to Building panel
                    navToBuilding(obj.target);
                  }
                }
              }}
              style={{ cursor: isLocked ? 'default' : 'pointer', display: 'inline-block' }}
            >
              <BuildingChip id={obj.target} size="small" />
            </span>
            {inModal && building?.unlockCondition?.tech && !researchedTechs.includes(building.unlockCondition.tech) && (
              <span className="obj-hint" style={{ color: 'var(--research-purple-light)' }}>
                (🔒 Requires:{' '}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isLocked && navToTech) {
                      navToTech(building.unlockCondition.tech);
                    }
                  }}
                  style={{ cursor: isLocked ? 'default' : 'pointer', display: 'inline-block' }}
                >
                  <TechChip id={building.unlockCondition.tech} size="small" />
                </span>
                {' '}← Research this first)
              </span>
            )}
            {inModal && building?.unlockCondition?.tech && researchedTechs.includes(building.unlockCondition.tech) && (
              <span className="obj-hint" style={{ color: 'var(--success)' }}>
                (✓ Unlocked - Click to build)
              </span>
            )}
          </>
        );
        break;
      }
      case 'resource': {
        const resource = resources[obj.target];
        // Find buildings that produce this resource
        const producers = Object.values(buildings)
          .filter(b => b.baseProduction && b.baseProduction[obj.target])
          .slice(0, 2);
        
        content = (
          <>
            Gather {obj.count?.toLocaleString()}{' '}
            <ResourceChip resourceId={obj.target} size="small" showName />
            {inModal && producers.length > 0 && (
              <span className="obj-hint">
                (From: {producers.map((p, i) => (
                  <React.Fragment key={p.id}>
                    <BuildingChip id={p.id} size="small" showIcon showName={false} />
                    {i < producers.length - 1 ? ', ' : ''}
                  </React.Fragment>
                ))})
              </span>
            )}
          </>
        );
        break;
      }
      case 'tech': {
        const tech = technologies[obj.target];
        // Get prerequisites
        const prereqs = tech?.requires || [];

        content = (
          <>
            Research{' '}
            <span
              onClick={(e) => {
                e.stopPropagation();
                if (!isLocked && navToTech) {
                  navToTech(obj.target);
                }
              }}
              style={{ cursor: isLocked ? 'default' : 'pointer', display: 'inline-block' }}
            >
              <TechChip id={obj.target} size="small" />
            </span>
            {inModal && prereqs.length > 0 && (
              <span className="obj-hint">
                (Prereqs: {prereqs.slice(0, 2).map((p, i) => (
                  <React.Fragment key={p}>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isLocked && navToTech) {
                          navToTech(p);
                        }
                      }}
                      style={{ cursor: isLocked ? 'default' : 'pointer', display: 'inline-block' }}
                    >
                      <TechChip id={p} size="small" showIcon showName={false} />
                    </span>
                    {i < Math.min(prereqs.length, 2) - 1 ? ' → ' : ''}
                  </React.Fragment>
                ))}
                {prereqs.length > 2 && ` +${prereqs.length - 2} more`})
              </span>
            )}
          </>
        );
        break;
      }
      case 'buildings_total':
        content = (
          <>
            Own {obj.count} total buildings
            {inModal && (
              <span className="obj-hint">(Any type counts)</span>
            )}
          </>
        );
        break;
      case 'tiles_explored':
        content = (
          <span
            onClick={(e) => {
              e.stopPropagation();
              if (!isLocked && navToWorldMap) {
                navToWorldMap();
              }
            }}
            style={{ cursor: isLocked ? 'default' : 'pointer' }}
          >
            Explore {obj.count} tiles
            {inModal && (
              <span className="obj-hint" style={{ cursor: 'default' }}> (Discover tiles on World Map)</span>
            )}
          </span>
        );
        break;
      case 'tiles_built':
        content = (
          <span
            onClick={(e) => {
              e.stopPropagation();
              if (!isLocked && navToWorldMap) {
                navToWorldMap();
              }
            }}
            style={{ cursor: isLocked ? 'default' : 'pointer' }}
          >
            Build on {obj.count} tiles
            {inModal && (
              <span className="obj-hint" style={{ cursor: 'default' }}> (Construct buildings on World Map tiles)</span>
            )}
          </span>
        );
        break;
      case 'expeditions_total':
        content = (
          <span
            onClick={(e) => {
              e.stopPropagation();
              if (!isLocked && navToWorldMap) {
                navToWorldMap();
              }
            }}
            style={{ cursor: isLocked ? 'default' : 'pointer' }}
          >
            Complete {obj.count} expeditions
            {inModal && (
              <span className="obj-hint" style={{ cursor: 'default' }}> (Any biome counts)</span>
            )}
          </span>
        );
        break;
      case 'expeditions_biome': {
        const biomeName = obj.target.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
        content = (
          <span
            onClick={(e) => {
              e.stopPropagation();
              if (!isLocked && navToWorldMap) {
                navToWorldMap();
              }
            }}
            style={{ cursor: isLocked ? 'default' : 'pointer' }}
          >
            Complete {obj.count} {biomeName} expeditions
          </span>
        );
        break;
      }
      case 'kingdom_tier':
        content = `Reach Kingdom Tier ${obj.count}`;
        break;
      case 'dungeons_cleared':
        content = (
          <>
            Clear {obj.count} dungeons
            {inModal && (
              <span className="obj-hint"> (Complete any dungeon)</span>
            )}
          </>
        );
        break;
      case 'dungeon_tier_cleared': {
        const tierNames = {
          tier1: 'Ancient Ruins',
          tier2: 'Dark Catacombs',
          tier3: 'Corrupted Temple',
          tier4: 'Abyssal Depths',
          tier5: 'Void Citadel'
        };
        const tierName = tierNames[obj.target] || obj.target;
        content = (
          <>
            Clear {tierName}
            {obj.count > 1 && ` ${obj.count} times`}
            {inModal && (
              <span className="obj-hint"> (Defeat the boss)</span>
            )}
          </>
        );
        break;
      }
      case 'bosses_defeated':
        content = (
          <>
            Defeat {obj.count} dungeon bosses
            {inModal && (
              <span className="obj-hint"> (Any dungeon tier)</span>
            )}
          </>
        );
        break;
      case 'character_level':
        content = (
          <>
            Reach level {obj.count}
            {inModal && (
              <span className="obj-hint"> (Gain XP from combat)</span>
            )}
          </>
        );
        break;
      case 'techs_researched':
      case 'tech_count':
        content = (
          <>
            Research {obj.count} technologies
            {inModal && (
              <span className="obj-hint"> (Any technology)</span>
            )}
          </>
        );
        break;
      case 'equipment_rarity_owned': {
        const rarityColors = {
          common: '#9ca3af',
          uncommon: '#22c55e',
          rare: '#3b82f6',
          epic: '#a855f7',
          legendary: '#fbbf24',
          mythic: '#ef4444'
        };
        const rarity = obj.rarity || obj.target; // Support both formats
        const color = rarityColors[rarity] || '#f0e6d8';
        content = (
          <>
            Own {obj.count}{' '}
            <span style={{ color, fontWeight: 'bold', textTransform: 'capitalize' }}>{rarity}</span>
            + equipment
            {inModal && (
              <span className="obj-hint"> (Find in dungeons)</span>
            )}
          </>
        );
        break;
      }
      case 'equipment_rarity_min': {
        const rarityColors = {
          common: '#9ca3af',
          uncommon: '#22c55e',
          rare: '#3b82f6',
          epic: '#a855f7',
          legendary: '#fbbf24',
          mythic: '#ef4444'
        };
        const color = rarityColors[obj.target] || '#f0e6d8';
        content = (
          <>
            Own equipment of{' '}
            <span style={{ color, fontWeight: 'bold', textTransform: 'capitalize' }}>{obj.target}</span>
            {' '}rarity or higher
            {inModal && (
              <span className="obj-hint"> (Find in dungeons)</span>
            )}
          </>
        );
        break;
      }
      case 'equipment_max_stars':
        content = (
          <>
            Enhance equipment to {obj.count}★
            {inModal && (
              <span className="obj-hint"> (Use Star Force)</span>
            )}
          </>
        );
        break;
      case 'equipment_stars_15_count':
        content = (
          <>
            Have {obj.count} equipment at 15★
            {inModal && (
              <span className="obj-hint"> (Max Star Force)</span>
            )}
          </>
        );
        break;
      case 'equipment_slots_filled':
        content = (
          <>
            Fill {obj.count} equipment slots
            {inModal && (
              <span className="obj-hint"> (Equip items)</span>
            )}
          </>
        );
        break;
      case 'equipment_scrolled':
        content = (
          <>
            Apply scrolls to {obj.count} equipment
            {inModal && (
              <span className="obj-hint"> (Use Forge tab)</span>
            )}
          </>
        );
        break;
      case 'great_works':
        content = (
          <>
            Build {obj.count} Great Works
            {inModal && (
              <span className="obj-hint"> (Place on world map)</span>
            )}
          </>
        );
        break;
      case 'wonders':
        content = (
          <>
            Build {obj.count} Wonders
            {inModal && (
              <span className="obj-hint"> (In Monuments tab)</span>
            )}
          </>
        );
        break;
      case 'outposts':
        content = (
          <>
            Build {obj.count} Outposts
            {inModal && (
              <span className="obj-hint"> (On world map tiles)</span>
            )}
          </>
        );
        break;
      case 'mercenaries_hired':
        content = (
          <>
            Hire {obj.count} mercenaries
            {inModal && (
              <span className="obj-hint"> (From Outposts)</span>
            )}
          </>
        );
        break;
      case 'mercenary_missions':
        content = (
          <>
            Complete {obj.count} mercenary missions
            {inModal && (
              <span className="obj-hint"> (Send squads from Outposts)</span>
            )}
          </>
        );
        break;
      case 'trade_routes':
        content = (
          <>
            Build {obj.count} trade route segments
            {inModal && (
              <span className="obj-hint"> (Connect tiles to Kingdom)</span>
            )}
          </>
        );
        break;
      case 'unique_biomes_expeditions':
        content = (
          <>
            Complete expeditions in {obj.count} unique biomes
            {inModal && (
              <span className="obj-hint"> (Different biome types)</span>
            )}
          </>
        );
        break;
      case 'main_quests_completed':
        content = (
          <>
            Complete {obj.count} main quests
            {inModal && (
              <span className="obj-hint"> (Story progression)</span>
            )}
          </>
        );
        break;
      case 'life_skill_level': {
        const skillName = obj.target ? obj.target.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim() : 'any skill';
        content = (
          <>
            Reach level {obj.count} in {skillName}
            {inModal && (
              <span className="obj-hint"> (Gather resources)</span>
            )}
          </>
        );
        break;
      }
      case 'crafting_completed':
        content = (
          <>
            Craft {obj.count} items
            {inModal && (
              <span className="obj-hint"> (Use Crafting tab)</span>
            )}
          </>
        );
        break;
      case 'advisors_hired':
        content = (
          <>
            Hire {obj.count} advisors
            {inModal && (
              <span className="obj-hint"> (Open Advisors panel)</span>
            )}
          </>
        );
        break;
      case 'beacons_discovered':
        content = (
          <>
            Discover {obj.count} teleport beacons
            {inModal && (
              <span className="obj-hint"> (Explore the map)</span>
            )}
          </>
        );
        break;
      case 'encounters_cleared':
        content = (
          <>
            Clear {obj.count} encounters
            {inModal && (
              <span className="obj-hint"> (Combat zones on map)</span>
            )}
          </>
        );
        break;
      default:
        // Fallback: format the type name nicely
        content = obj.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) + (obj.count > 1 ? ` (${obj.count})` : '');
    }

    return (
      <div className={`quest-obj ${done ? 'done' : ''} ${inModal ? 'quest-obj-rich' : ''}`}>
        <span className="obj-icon">{done ? '✅' : isLocked ? '🔒' : '⬜'}</span>
        <span className="obj-text">{content}</span>
        {!isLocked && (
          <span className="obj-progress">{current.toLocaleString()}/{target.toLocaleString()}</span>
        )}
      </div>
    );
  };

  // Render quest details (used in both panel and modal)
  const renderQuestDetails = (quest, qid, inModal = false) => {
    const status = getQuestStatus(qid);
    const chapter = quest.chapter || 1;
    const scaledRewards = getScaledQuestRewards(quest.rewards, chapter);

    return (
      <div className="quest-content">
        <div className="quest-name">{quest.name}</div>
        <div className="quest-desc">{quest.description}</div>

        <div className="quest-objectives">
          {quest.objectives.map((obj, i) => (
            <React.Fragment key={i}>
              {renderObjective(obj, status, inModal, chapter, { onNavigateToBuilding, onNavigateToTech, onNavigateToWorldMap })}
            </React.Fragment>
          ))}
        </div>

        <div className="quest-rewards">
          <span className="rewards-label">Rewards:</span>
          {Object.entries(scaledRewards).map(([res, amt]) => (
            <ResourceChip
              key={res}
              resourceId={res}
              amount={amt}
              size="small"
            />
          ))}
        </div>
      </div>
    );
  };

  // Get status for side quests
  const getSideQuestStatus = (qid) => {
    if (completedSideQuests.includes(qid)) return 'done';
    if (availableSideQuests.some(q => q.id === qid)) return 'available';
    return 'locked';
  };

  // Get status for extra quests
  const getExtraQuestStatus = (qid) => {
    if (completedExtraQuests.includes(qid)) return 'done';
    if (availableExtraQuests.some(q => q.id === qid)) return 'available';
    return 'locked';
  };

  // Check if a side/extra quest is complete
  const checkQuestComplete = (quest) => {
    if (!quest?.objectives) return false;
    return quest.objectives.every(obj => {
      const { done } = checkObjective(obj, 'available', quest.chapter || 1);
      return done;
    });
  };

  // Render side quest details
  const renderSideQuestDetails = (quest, inModal = false) => {
    const status = getSideQuestStatus(quest.id);
    const chain = sideQuestChains[quest.chain];

    return (
      <div className="quest-content">
        <div className="quest-chain-badge" style={{ background: 'rgba(139, 92, 246, 0.2)', borderColor: 'var(--research-purple)' }}>
          {chain?.icon} {chain?.name}
        </div>
        <div className="quest-name">{quest.name}</div>
        <div className="quest-desc">{quest.description}</div>

        <div className="quest-objectives">
          {quest.objectives.map((obj, i) => (
            <React.Fragment key={i}>
              {renderObjective(obj, status === 'locked' ? 'locked' : 'current', inModal, 1, { onNavigateToBuilding, onNavigateToTech, onNavigateToWorldMap })}
            </React.Fragment>
          ))}
        </div>

        <div className="quest-rewards">
          <span className="rewards-label">Rewards:</span>
          {Object.entries(quest.rewards || {}).map(([res, amt]) => (
            <ResourceChip key={res} resourceId={res} amount={amt} size="small" />
          ))}
        </div>
      </div>
    );
  };

  // Render extra quest details
  const renderExtraQuestDetails = (quest, inModal = false) => {
    const status = getExtraQuestStatus(quest.id);
    const category = extraQuestCategories[quest.category];

    return (
      <div className="quest-content">
        <div className="quest-chain-badge" style={{ background: 'rgba(234, 179, 8, 0.2)', borderColor: 'var(--accent-gold)' }}>
          {category?.icon} {category?.name}
        </div>
        <div className="quest-name">{quest.name}</div>
        <div className="quest-desc">{quest.description}</div>

        <div className="quest-objectives">
          {quest.objectives.map((obj, i) => (
            <React.Fragment key={i}>
              {renderObjective(obj, status === 'locked' ? 'locked' : 'current', inModal, 1, { onNavigateToBuilding, onNavigateToTech, onNavigateToWorldMap })}
            </React.Fragment>
          ))}
        </div>

        <div className="quest-rewards">
          <span className="rewards-label">Rewards:</span>
          {Object.entries(quest.rewards || {}).map(([res, amt]) => (
            <ResourceChip key={res} resourceId={res} amount={amt} size="small" />
          ))}
        </div>
      </div>
    );
  };

  // Quest Log Modal with tabs for Main/Side/Extra
  const renderQuestLogModal = () => {
    const totalCounts = getTotalQuestCounts();
    const totalCompleted = completedQuests.length + completedSideQuests.length + completedExtraQuests.length;

    return (
      <div className="modal-overlay" onClick={() => setShowQuestLog(false)}>
        <div className="quest-log-modal" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="ql-header">
            <div className="ql-header-title">
              <span className="ql-header-icon">📜</span>
              <h2>Quest Log</h2>
            </div>
            <div className="ql-header-progress">
              <div className="ql-progress-bar">
                <div
                  className="ql-progress-fill"
                  style={{ width: `${(totalCompleted / totalCounts.total) * 100}%` }}
                />
              </div>
              <span className="ql-progress-text">{totalCompleted}/{totalCounts.total}</span>
            </div>
            <button className="close-btn" onClick={() => setShowQuestLog(false)}>✕</button>
          </div>

          {/* Category Tabs */}
          <div className="ql-category-tabs">
            <button
              className={`ql-category-tab ${questLogTab === 'main' ? 'active' : ''}`}
              onClick={() => setQuestLogTab('main')}
            >
              <span className="ql-tab-icon">📖</span>
              <span className="ql-tab-label">Main Story</span>
              <span className="ql-tab-count">{completedQuests.length}/{totalCounts.main}</span>
            </button>
            <button
              className={`ql-category-tab ${questLogTab === 'side' ? 'active' : ''}`}
              onClick={() => setQuestLogTab('side')}
            >
              <span className="ql-tab-icon">🗺️</span>
              <span className="ql-tab-label">Side Quests</span>
              <span className="ql-tab-count">{completedSideQuests.length}/{totalCounts.side}</span>
            </button>
            <button
              className={`ql-category-tab ${questLogTab === 'extra' ? 'active' : ''}`}
              onClick={() => setQuestLogTab('extra')}
            >
              <span className="ql-tab-icon">🏆</span>
              <span className="ql-tab-label">Achievements</span>
              <span className="ql-tab-count">{completedExtraQuests.length}/{totalCounts.extra}</span>
            </button>
          </div>

          {/* Main Quest Tab */}
          {questLogTab === 'main' && renderMainQuestTab()}

          {/* Side Quest Tab */}
          {questLogTab === 'side' && renderSideQuestTab()}

          {/* Extra Quest Tab */}
          {questLogTab === 'extra' && renderExtraQuestTab()}
        </div>
      </div>
    );
  };

  // Main Quest Tab Content
  const renderMainQuestTab = () => {
    const chapterQuests = getMainQuestsByChapter(selectedChapter);
    const selectedChapterData = chapters[selectedChapter];
    const chapterProgress = getMainChapterProgress(completedQuests, selectedChapter);

    return (
      <>
        {/* Chapter Navigation */}
        <div className="ql-chapter-nav">
          {Object.entries(chapters).map(([num, chapter]) => {
            const progress = getMainChapterProgress(completedQuests, parseInt(num));
            const isComplete = progress.completed === progress.total;
            const isActive = selectedChapter === parseInt(num);
            const hasProgress = progress.completed > 0;

            return (
              <button
                key={num}
                className={`ql-chapter-btn ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''} ${hasProgress && !isComplete ? 'in-progress' : ''}`}
                onClick={() => setSelectedChapter(parseInt(num))}
                title={`${chapter.name}: ${progress.completed}/${progress.total}`}
              >
                <span className="ql-chapter-icon">{CHAPTER_ICONS[num]}</span>
                <span className="ql-chapter-num">{num}</span>
                {isComplete && <span className="ql-chapter-check">✔</span>}
              </button>
            );
          })}
        </div>

        {/* Chapter Title Bar */}
        <div className="ql-chapter-title-bar">
          <span className="ql-chapter-title-icon">{CHAPTER_ICONS[selectedChapter]}</span>
          <div className="ql-chapter-title-text">
            <span className="ql-chapter-title-name">Chapter {selectedChapter}: {selectedChapterData?.name}</span>
            <span className="ql-chapter-title-desc">{selectedChapterData?.description}</span>
          </div>
          <div className="ql-chapter-title-progress">
            <span className="ql-chapter-title-count">{chapterProgress.completed}/{chapterProgress.total}</span>
            <div className="ql-mini-progress">
              <div
                className="ql-mini-progress-fill"
                style={{ width: `${(chapterProgress.completed / chapterProgress.total) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="ql-body">
          {/* Quest List */}
          <div className="ql-list">
            {chapterQuests.map((quest) => {
              const status = getQuestStatus(quest.id);
              const idx = questIds.indexOf(quest.id);
              const showName = status !== 'locked' || idx <= currentIndex + 3;
              const questNum = idx + 1;

              return (
                <div
                  key={quest.id}
                  className={`ql-item ${status} ${selectedMainQuest === quest.id ? 'selected' : ''}`}
                  onClick={() => (status !== 'locked' || showName) && setSelectedMainQuest(quest.id)}
                >
                  <div className="ql-item-indicator">
                    {status === 'done' && <span className="ql-done-icon">✔</span>}
                    {status === 'current' && <span className="ql-current-icon">▶</span>}
                    {status === 'locked' && <span className="ql-locked-icon">🔒</span>}
                  </div>
                  <div className="ql-item-content">
                    <span className="ql-item-num">#{questNum}</span>
                    <span className="ql-item-name">{showName ? quest.name : '???'}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quest Details */}
          <div className="ql-details">
            {selectedMainQuest && mainQuests[selectedMainQuest] ? (
              <div className="ql-details-inner">
                <div className={`ql-detail-status ${getQuestStatus(selectedMainQuest)}`}>
                  {getQuestStatus(selectedMainQuest) === 'done' && '✅ Completed'}
                  {getQuestStatus(selectedMainQuest) === 'current' && '📍 In Progress'}
                  {getQuestStatus(selectedMainQuest) === 'locked' && '🔒 Locked'}
                </div>
                {renderQuestDetails(mainQuests[selectedMainQuest], selectedMainQuest, true)}
              </div>
            ) : (
              <div className="ql-empty">
                <span className="ql-empty-icon">📋</span>
                <p>Select a quest to view details</p>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  // Side Quest Tab Content
  const renderSideQuestTab = () => {
    const chainQuests = getSideQuestsByChain(selectedChain);
    const chainData = sideQuestChains[selectedChain];
    const chainProgress = getSideChainProgress(completedSideQuests, selectedChain);

    return (
      <>
        {/* Chain Navigation */}
        <div className="ql-chain-nav">
          {Object.entries(sideQuestChains).map(([chainId, chain]) => {
            const progress = getSideChainProgress(completedSideQuests, chainId);
            const isComplete = progress.completed === progress.total;
            const isActive = selectedChain === chainId;
            const hasProgress = progress.completed > 0;

            return (
              <button
                key={chainId}
                className={`ql-chain-btn ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''} ${hasProgress && !isComplete ? 'in-progress' : ''}`}
                onClick={() => setSelectedChain(chainId)}
                title={`${chain.name}: ${progress.completed}/${progress.total}`}
              >
                <span className="ql-chain-icon">{chain.icon}</span>
                <span className="ql-chain-name">{chain.name}</span>
                <span className="ql-chain-count">{progress.completed}/{progress.total}</span>
              </button>
            );
          })}
        </div>

        {/* Chain Title Bar */}
        <div className="ql-chapter-title-bar">
          <span className="ql-chapter-title-icon">{chainData?.icon}</span>
          <div className="ql-chapter-title-text">
            <span className="ql-chapter-title-name">{chainData?.name}</span>
            <span className="ql-chapter-title-desc">{chainData?.description}</span>
          </div>
          <div className="ql-chapter-title-progress">
            <span className="ql-chapter-title-count">{chainProgress.completed}/{chainProgress.total}</span>
            <div className="ql-mini-progress">
              <div
                className="ql-mini-progress-fill"
                style={{ width: `${chainProgress.total > 0 ? (chainProgress.completed / chainProgress.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="ql-body">
          {/* Quest List */}
          <div className="ql-list">
            {chainQuests.map((quest, idx) => {
              const status = getSideQuestStatus(quest.id);
              const isComplete = checkQuestComplete(quest);

              return (
                <div
                  key={quest.id}
                  className={`ql-item ${status} ${selectedSideQuest === quest.id ? 'selected' : ''}`}
                  onClick={() => status !== 'locked' && setSelectedSideQuest(quest.id)}
                >
                  <div className="ql-item-indicator">
                    {status === 'done' && <span className="ql-done-icon">✔</span>}
                    {status === 'available' && <span className="ql-current-icon">⚡</span>}
                    {status === 'locked' && <span className="ql-locked-icon">🔒</span>}
                  </div>
                  <div className="ql-item-content">
                    <span className="ql-item-num">#{idx + 1}</span>
                    <span className="ql-item-name">{status !== 'locked' ? quest.name : '???'}</span>
                    {status === 'available' && isComplete && <span className="ql-item-ready">✨</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quest Details */}
          <div className="ql-details">
            {selectedSideQuest && sideQuests[selectedSideQuest] ? (
              <div className="ql-details-inner">
                {/* Header row with status and track button inline */}
                <div className="ql-detail-header">
                  <div className={`ql-detail-status ${getSideQuestStatus(selectedSideQuest)}`}>
                    {getSideQuestStatus(selectedSideQuest) === 'done' && '✅ Completed'}
                    {getSideQuestStatus(selectedSideQuest) === 'available' && '⚡ Available'}
                    {getSideQuestStatus(selectedSideQuest) === 'locked' && '🔒 Locked'}
                  </div>
                  {/* Track/Untrack Button for Side Quests - inline */}
                  {getSideQuestStatus(selectedSideQuest) === 'available' && (
                    <button
                      className={`ql-track-btn ${trackedQuest?.id === selectedSideQuest ? 'tracked' : ''}`}
                      onClick={() => {
                        if (trackedQuest?.id === selectedSideQuest) {
                          setTrackedQuest(null);
                        } else {
                          setTrackedQuest({ id: selectedSideQuest, type: 'side' });
                          setShowQuestLog(false);
                        }
                      }}
                    >
                      {trackedQuest?.id === selectedSideQuest ? '📍 Tracking' : '📌 Track'}
                    </button>
                  )}
                </div>
                {renderSideQuestDetails(sideQuests[selectedSideQuest], true)}

                {/* Complete Button for Side Quests */}
                {getSideQuestStatus(selectedSideQuest) === 'available' && checkQuestComplete(sideQuests[selectedSideQuest]) && (
                  <button
                    className="ql-complete-btn"
                    onClick={() => {
                      if (completeSideQuest?.(selectedSideQuest)) {
                        onNotification?.('🎉 Side Quest Complete!', 'success');
                      }
                    }}
                  >
                    ✅ Complete Quest
                  </button>
                )}
              </div>
            ) : (
              <div className="ql-empty">
                <span className="ql-empty-icon">🗺️</span>
                <p>Select a side quest to view details</p>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  // Extra Quest Tab Content
  const renderExtraQuestTab = () => {
    const categoryQuests = getExtraQuestsByCategory(selectedCategory);
    const categoryData = extraQuestCategories[selectedCategory];
    const categoryProgress = getExtraCategoryProgress(completedExtraQuests, selectedCategory);

    return (
      <>
        {/* Category Navigation */}
        <div className="ql-chain-nav">
          {Object.entries(extraQuestCategories).map(([catId, category]) => {
            const progress = getExtraCategoryProgress(completedExtraQuests, catId);
            const isComplete = progress.completed === progress.total;
            const isActive = selectedCategory === catId;
            const hasProgress = progress.completed > 0;

            return (
              <button
                key={catId}
                className={`ql-chain-btn ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''} ${hasProgress && !isComplete ? 'in-progress' : ''}`}
                onClick={() => setSelectedCategory(catId)}
                title={`${category.name}: ${progress.completed}/${progress.total}`}
              >
                <span className="ql-chain-icon">{category.icon}</span>
                <span className="ql-chain-name">{category.name}</span>
                <span className="ql-chain-count">{progress.completed}/{progress.total}</span>
              </button>
            );
          })}
        </div>

        {/* Category Title Bar */}
        <div className="ql-chapter-title-bar">
          <span className="ql-chapter-title-icon">{categoryData?.icon}</span>
          <div className="ql-chapter-title-text">
            <span className="ql-chapter-title-name">{categoryData?.name}</span>
            <span className="ql-chapter-title-desc">{categoryData?.description}</span>
          </div>
          <div className="ql-chapter-title-progress">
            <span className="ql-chapter-title-count">{categoryProgress.completed}/{categoryProgress.total}</span>
            <div className="ql-mini-progress">
              <div
                className="ql-mini-progress-fill"
                style={{ width: `${categoryProgress.total > 0 ? (categoryProgress.completed / categoryProgress.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="ql-body">
          {/* Quest List */}
          <div className="ql-list">
            {categoryQuests.map((quest, idx) => {
              const status = getExtraQuestStatus(quest.id);
              const isComplete = checkQuestComplete(quest);

              return (
                <div
                  key={quest.id}
                  className={`ql-item ${status} ${selectedExtraQuest === quest.id ? 'selected' : ''}`}
                  onClick={() => status !== 'locked' && setSelectedExtraQuest(quest.id)}
                >
                  <div className="ql-item-indicator">
                    {status === 'done' && <span className="ql-done-icon">✔</span>}
                    {status === 'available' && <span className="ql-current-icon">🏆</span>}
                    {status === 'locked' && <span className="ql-locked-icon">❓</span>}
                  </div>
                  <div className="ql-item-content">
                    <span className="ql-item-name">{status !== 'locked' ? quest.name : '???'}</span>
                    {status === 'available' && isComplete && <span className="ql-item-ready">✨</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quest Details */}
          <div className="ql-details">
            {selectedExtraQuest && extraQuests[selectedExtraQuest] ? (
              <div className="ql-details-inner">
                {/* Header row with status and track button inline */}
                <div className="ql-detail-header">
                  <div className={`ql-detail-status ${getExtraQuestStatus(selectedExtraQuest)}`}>
                    {getExtraQuestStatus(selectedExtraQuest) === 'done' && '✅ Completed'}
                    {getExtraQuestStatus(selectedExtraQuest) === 'available' && '🏆 Available'}
                    {getExtraQuestStatus(selectedExtraQuest) === 'locked' && '❓ Hidden'}
                  </div>
                  {/* Track/Untrack Button for Extra Quests - inline */}
                  {getExtraQuestStatus(selectedExtraQuest) === 'available' && (
                    <button
                      className={`ql-track-btn ${trackedQuest?.id === selectedExtraQuest ? 'tracked' : ''}`}
                      onClick={() => {
                        if (trackedQuest?.id === selectedExtraQuest) {
                          setTrackedQuest(null);
                        } else {
                          setTrackedQuest({ id: selectedExtraQuest, type: 'extra' });
                          setShowQuestLog(false);
                        }
                      }}
                    >
                      {trackedQuest?.id === selectedExtraQuest ? '📍 Tracking' : '📌 Track'}
                    </button>
                  )}
                </div>
                {renderExtraQuestDetails(extraQuests[selectedExtraQuest], true)}

                {/* Complete Button for Extra Quests */}
                {getExtraQuestStatus(selectedExtraQuest) === 'available' && checkQuestComplete(extraQuests[selectedExtraQuest]) && (
                  <button
                    className="ql-complete-btn"
                    onClick={() => {
                      if (completeExtraQuest?.(selectedExtraQuest)) {
                        onNotification?.('🎉 Achievement Unlocked!', 'success');
                      }
                    }}
                  >
                    🏆 Claim Reward
                  </button>
                )}
              </div>
            ) : (
              <div className="ql-empty">
                <span className="ql-empty-icon">🏆</span>
                <p>Select an achievement to view details</p>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  // Determine what quest to display in the panel
  // Priority: 1. Tracked quest (side/extra), 2. Active main quest, 3. All complete message
  const getDisplayQuest = () => {
    // If tracking a side quest
    if (trackedQuest?.type === 'side' && sideQuests[trackedQuest.id]) {
      const quest = sideQuests[trackedQuest.id];
      const isComplete = checkQuestComplete(quest);
      const chain = sideQuestChains[quest.chain];
      return {
        type: 'side',
        quest,
        isComplete,
        icon: chain?.icon || '🗺️',
        label: chain?.name || 'Side Quest',
      };
    }

    // If tracking an extra quest
    if (trackedQuest?.type === 'extra' && extraQuests[trackedQuest.id]) {
      const quest = extraQuests[trackedQuest.id];
      const isComplete = checkQuestComplete(quest);
      const category = extraQuestCategories[quest.category];
      return {
        type: 'extra',
        quest,
        isComplete,
        icon: category?.icon || '🏆',
        label: category?.name || 'Achievement',
      };
    }

    // Default to main quest
    if (activeQuest && mainQuests[activeQuest]) {
      return {
        type: 'main',
        quest: mainQuests[activeQuest],
        isComplete: isQuestComplete?.(activeQuest),
        icon: '📖',
        label: 'Main Quest',
      };
    }

    return null;
  };

  const displayQuest = getDisplayQuest();

  // Render tracked/main quest unpin button
  const renderUnpinButton = () => {
    if (!trackedQuest) return null;
    return (
      <button
        className="quest-unpin-btn"
        onClick={() => setTrackedQuest(null)}
        title="Stop tracking this quest"
      >
        📍
      </button>
    );
  };

  // No quest to display
  if (!displayQuest) {
    return (
      <div className="quest-panel">
        <div className="quest-header">
          <h3>📜 Quest</h3>
          <button className="quest-log-btn" onClick={() => { setShowQuestLog(true); setSelectedMainQuest(questIds[questIds.length - 1]); }}>
            📋 {completedQuests.length}/{questIds.length}
          </button>
        </div>
        <div className="quest-complete-msg">🎉 All quests completed!</div>
        {showQuestLog && renderQuestLogModal()}
      </div>
    );
  }

  // Render quest completion handler
  const handleCompleteQuest = () => {
    if (displayQuest.type === 'main') {
      if (completeQuest(activeQuest)) {
        onNotification?.('🎉 Quest Complete!', 'success');
      }
    } else if (displayQuest.type === 'side') {
      if (completeSideQuest(trackedQuest.id)) {
        onNotification?.('🎉 Side Quest Complete!', 'success');
      }
    } else if (displayQuest.type === 'extra') {
      if (completeExtraQuest(trackedQuest.id)) {
        onNotification?.('🏆 Achievement Unlocked!', 'success');
      }
    }
  };

  return (
    <div className="quest-panel">
      <div className="quest-header">
        <h3>
          {trackedQuest ? (
            <span className="quest-type-badge">
              {displayQuest.icon} {displayQuest.label}
            </span>
          ) : (
            '📜 Quest'
          )}
        </h3>
        <div className="quest-header-actions">
          {renderUnpinButton()}
          <button className="quest-log-btn" onClick={() => {
            setShowQuestLog(true);
            if (trackedQuest?.type === 'side') {
              setQuestLogTab('side');
              setSelectedSideQuest(trackedQuest.id);
              setSelectedChain(sideQuests[trackedQuest.id]?.chain || 'explorer');
            } else if (trackedQuest?.type === 'extra') {
              setQuestLogTab('extra');
              setSelectedExtraQuest(trackedQuest.id);
              setSelectedCategory(extraQuests[trackedQuest.id]?.category || 'milestone');
            } else {
              setQuestLogTab('main');
              setSelectedMainQuest(activeQuest);
              setSelectedChapter(mainQuests[activeQuest]?.chapter || 1);
            }
          }}>
            📋 {currentIndex + 1}/{questIds.length}
          </button>
        </div>
      </div>

      {/* Render quest details based on type */}
      {displayQuest.type === 'main' && renderQuestDetails(displayQuest.quest, activeQuest)}
      {displayQuest.type === 'side' && renderSideQuestDetails(displayQuest.quest, false)}
      {displayQuest.type === 'extra' && renderExtraQuestDetails(displayQuest.quest, false)}

      {displayQuest.isComplete && (
        <button className="quest-complete-btn" onClick={handleCompleteQuest}>
          {displayQuest.type === 'extra' ? '🏆 Claim Reward' : '✅ Complete Quest'}
        </button>
      )}

      {showQuestLog && renderQuestLogModal()}
    </div>
  );
}

export default QuestPanel;
